<template>
  <div class="content mb-5">
    <b-row class="mb-3 d-flex align-items-center">
      <b-col cols="12" sm="8">
        <h2 class="mb-2">
          {{ this.$t("Protection and Filtering at a Glance") }}
        </h2>
      </b-col>
    </b-row>
    <b-row class="mb-4 d-flex">
      <b-col md="8">
        {{ this.$t("Total requests for") }}
        <span class="font-weight-bold red">{{ fullDate }}</span>
      </b-col>
      <b-col md="4">
        <h2 class="text-truncate text-right" v-if="!loading">
          {{ mspName }}
        </h2>
        <b-skeleton
          v-else
          animation="wave"
          height="35px"
          width="100%"
        ></b-skeleton>
      </b-col>
    </b-row>

    <b-row class="mb-2">
      <b-col cols="12" sm="6" md="6" lg="3" class="mb-3 pr-md-2">
        <t-card-trend
          data-test-id="dashboard"
          :title="this.$t('Requests')"
          :figure="mspStats.overview.allowed + mspStats.overview.blocked || 0"
          :loading="loading"
        />
      </b-col>
      <b-col cols="12" sm="6" md="6" lg="3" class="mb-3 px-md-2">
        <t-card-trend
          data-test-id="dashboard"
          :title="this.$t('Average')"
          :figure="mspStats.overview.average || 0"
          :loading="loading"
        />
      </b-col>
      <b-col cols="12" sm="6" md="6" lg="3" class="mb-3 px-md-2">
        <t-card-trend
          data-test-id="dashboard"
          :title="this.$t('Allowed')"
          :figure="mspStats.overview.allowed || 0"
          :loading="loading"
        />
      </b-col>
      <b-col cols="12" sm="6" md="6" lg="3" class="mb-3 pl-md-2">
        <t-card-trend
          data-test-id="dashboard"
          :title="this.$t('Blocked')"
          :figure="mspStats.overview.blocked || 0"
          :loading="loading"
        />
      </b-col>
    </b-row>

    <b-row class="mb-3">
      <b-col>
        <t-quick-actions
          horizontal
          :title="this.$t('Global Actions')"
          :actions="quickActions.global"
          :loading="loading"
          @on-action-click="onQuickActionClick"
        />
      </b-col>
    </b-row>

    <section class="all-customers">
      <b-row class="mb-4">
        <b-col sm="12">
          <t-data-table
            data-test-id="dashboard-data-table"
            with-filter
            :loading="loading"
            :title="$t('All Customers')"
            :filter-placeholder="$t('Search Customers')"
            :table-data="users"
            :actions="tableActions"
            :columns-on-load="6"
            user-role="msp"
            @on-delete="onDelete"
            @on-add="onAdd"
            @on-row-clicked="onCustomerTableRowClick"
            @on-export="onExport"
            @export-headers="exportedHeaders"
          />
        </b-col>
      </b-row>
    </section>
  </div>
</template>

<route>
{
  "name": "MSP Overview",
  "meta": {
    "authorize" : [1, 2, 3]
  }
}
</route>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import dayjs from "dayjs";

export default {
  layout: "msp",
  data() {
    return {
      mspName: null,
      currentHeaders: null,
      chartLegend: "",
      selectedDate: "",
      yesterdayDate: "",
      loading: true,
      tableActions: {
        add: {
          enabled: true,
          actions: [
            {
              text: this.$t("Add Customer"),
              value: {
                modal: "t-add-customer",
                dataTestId: "modal-add-customer",
                title: this.$t("Add a customer"),
                subtitle: this.$t("New"),
                props: {
                  size: "lg",
                  okTitle: this.$t("Add Customer"),
                },
              },
            },
          ],
        },
        export: { enabled: true },
        import: { enabled: false },
        delete: { enabled: true },
      },
      users: {
        fields: [
          {
            value: "accountname",
            text: this.$t("Account Name"),
            sortable: true,
          },
          {
            value: "description",
            text: this.$t("Description"),
            sortable: true,
          },
          { value: "email", text: this.$t("Email Address"), sortable: true },
          { value: "created", text: this.$t("Date Created"), sortable: true },
          {
            value: "license_count",
            text: this.$t("License Count"),
            sortable: true,
          },
          { value: "lastlogin", text: this.$t("Last Login"), sortable: true },

          { value: "id", text: this.$t("ID Number"), sortable: true },
          { value: "uuid_str", text: this.$t("UUID"), sortable: true },
        ],
        items: [],
      },
      quickActions: {
        global: [
          {
            title: this.$t("Allow Domain"),
            testID: "dashboard-quick-actions-allow-domain",
            value: "allowDomainMSP",
            icon: "check-square",
            iconVariant: "secondary",
          },
          {
            title: this.$t("Block Domain"),
            testID: "dashboard-quick-actions-block-domain",
            value: "blockDomainMSP",
            icon: "x-square",
            iconVariant: "secondary",
          },
          {
            title: this.$t("Edit Default Policy"),
            testID: "dashboard-quick-actions-edit-default-policy",
            value: "editDefaultPolicy",
            icon: "shield",
            iconVariant: "secondary",
          },
        ],
      },
      chartColours: [
        "#058DC7",
        "#50b432",
        "#ed561b",
        "#edef00",
        "#24cbe5",
        "#64e572",
        "#ff9655",
        "#fff263",
        "#6af9c4",
        "#b2deff",
      ],
      chartData: {
        small: {
          highlight: "321",
          difference: -19,
          title: "Line",
          labels: Array.from({ length: 5 }, () =>
            Math.floor(Math.random() * 40)
          ),
          datasets: [
            {
              borderColor: this.$chartColours.allowed,
              backgroundColor: this.$chartColours.allowed,
              label: "Test Dataset 1",
              data: Array.from({ length: 5 }, () =>
                Math.floor(Math.random() * 40)
              ),
              fill: false,
            },
          ],
        },
      },
    };
  },
  computed: {
    ...mapGetters("stats", ["mspStats"]),
    ...mapGetters("users", ["userAccounts"]),
    ...mapGetters("authentication", [
      "role",
      "selectedUser",
      "mspId",
      "selectedMspUser",
      "authUser",
    ]),
    fullDate() {
      return dayjs(this.yesterdayDate).format("dddd, D MMMM YYYY ");
    },
    webTraffic() {
      return this.organiseWebTrafficChart(this.mspStats.recentTraffic.data);
    },
    topCategoriesAllowed() {
      return this.mspStats.topCategories.allowed.data.slice(0, 5);
    },
    topCategoriesBlocked() {
      return this.mspStats.topCategories.blocked.data.slice(0, 5);
    },
  },
  methods: {
    ...mapActions("stats", ["fetchMspOverview"]),
    ...mapActions("users", ["fetchCustomerAccounts"]),
    ...mapActions("toast", ["displayToast"]),
    ...mapActions("pageMeta", ["setPageTitle"]),
    ...mapMutations("modal", ["createModal", "showModal"]),
    ...mapActions("users", ["deleteCustomerAccounts"]),
    ...mapActions("authentication", ["setUser"]),
    ...mapActions("userInterface", ["fetchInterface"]),

    generateLegend(legend) {
      this.chartLegend = legend;
    },
    exportedHeaders(headers) {
      this.currentHeaders = headers;
    },
    onExport(items) {
      if (items.length) {
        const exportData = items.map((res) => {
          let payload = {};
          this.currentHeaders.forEach((headers) => {
            return (payload[`${headers.value}`] = res[`${headers.value}`]);
          });

          return payload;
        });
        this.$downloadCSV(exportData, "recent");
      } else {
        this.displayToast({
          title: this.$t("Failure"),
          message: this.$t("No recent traffic in table"),
          duration: 2000,
          variant: "danger",
        });
      }
    },
    onQuickActionClick(action) {
      switch (action.value) {
        case "allowDomainMSP":
          this.createModal({
            modal: "t-add-domain",
            dataTestId: "modal-add-domain-allow",
            title: this.$t("Allow a domain"),
            subtitle: this.$t("All accounts"),
            modalData: {
              type: "allow",
              toPolicy: false,
              isGlobal: false,
              isMSP: true,
              mspId: this.mspId,
            },
            props: {
              size: "lg",
              okTitle: this.$t("Allow Domain"),
            },
          });
          this.showModal();
          break;

        case "blockDomainMSP":
          this.createModal({
            modal: "t-add-domain",
            dataTestId: "modal-add-domain-block",
            title: this.$t("Block a domain"),
            subtitle: this.$t("All accounts"),
            modalData: {
              type: "block",
              toPolicy: false,
              isGlobal: false,
              isMSP: true,
              mspId: this.mspId,
            },
            props: {
              size: "lg",
              okTitle: this.$t("Block Domain"),
            },
          });
          this.showModal();
          break;

        case "editDefaultPolicy":
          this.$router.push("/msp/global_settings/global_default_policy");
          break;

        case "allowDomain":
          this.$router.push("/content_filtering/domains");
          break;

        case "blockLocation":
        case "addLocation":
          this.$router.push("/msp/environment/locations");
          break;

        case "customisePolicy":
          this.$router.push("/content_filtering/policies");
          break;

        default:
          break;
      }
    },
    onFilterUpdate(id) {
      this.selectedView = id;
    },
    onTableFilterUpdate(id) {
      this.selectedTableView = id;
    },
    logRowData(row) {
      alert(`Clicked row with ID: ${row.item.id}`);
    },
    topChartDataset(dataset) {
      return {
        labels: dataset.map((item) => item.name),
        datasets: [
          {
            data: dataset.map((item) => item.count),
            backgroundColor: this.chartColours,
            hoverOffset: 4,
          },
        ],
      };
    },
    organiseWebTrafficChart(data) {
      if (!data) return [];
      data.pop();
      return {
        labels: data.map((item) => dayjs(item.date).format("hh:mm")),
        datasets: [
          {
            label: "Allowed",
            borderColor: this.$chartColours.allowed,
            backgroundColor: this.$chartColours.allowed,
            data: data.map((item) => item.allowed),
            fill: false,
          },
          {
            label: "Blocked",
            borderColor: this.$chartColours.blocked,
            backgroundColor: this.$chartColours.blocked,
            data: data.map((item) => item.blocked),
            fill: false,
          },
        ],
      };
    },
    fetchMSPDashboardData() {
      if (this.mspId > 0) {
        return Promise.all([
          this.fetchMspOverview({
            mspId: this.mspId,
            date: this.yesterdayDate,
          }),
          this.fetchCustomerAccounts({ msp_id: this.mspId }),
        ]);
      }
    },
    async onDelete(users) {
      try {
        await this.deleteCustomerAccounts(users);
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Deleted: X", { item: users.length }),
          duration: 2000,
          variant: "success",
        });
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      }
    },
    onAdd(modal) {
      this.createModal(modal);
      this.showModal();
    },
    onCustomerTableRowClick(customer) {
      this.setUser(customer);
      localStorage.setItem("selectedCustomer", customer.id);
      this.$router.push("/customer/overview");
    },
  },
  watch: {
    async selectedDate() {
      this.loading = true;
      await this.fetchMSPDashboardData();
      this.loading = false;
    },
    userAccounts: {
      handler() {
        this.users.items = this.userAccounts;
      },
      immediate: true,
    },
  },
  async created() {
    this.setPageTitle(" " + this.$t("Overview"));
    this.selectedDate = dayjs().format("YYYY-MM-DD");
    this.yesterdayDate = dayjs().subtract(1, "day").format("YYYY-MM-DD");

    if (this.role === 1) {
      this.mspName = this.selectedMspUser.name;
    } else {
      this.mspName = this.authUser.mspAccountName;
    }
  },
  async mounted() {
    await this.fetchInterface({ mspId: this.mspId });
  },
};
</script>

<style scoped lang="scss">
.chart-color-ref {
  width: 12px;
  height: 12px;
  display: inline-block;
}
</style>
